import {
  aggregationFn_count,
  aggregationFn_max,
  aggregationFn_mean,
  aggregationFn_min,
  aggregationFn_sum,
  columnFacetingFeature,
  columnFilteringFeature,
  columnGroupingFeature,
  columnOrderingFeature,
  columnVisibilityFeature,
  createExpandedRowModel,
  createFacetedMinMaxValues,
  createFacetedRowModel,
  createFacetedUniqueValues,
  createFilteredRowModel,
  createGroupedRowModel,
  createPaginatedRowModel,
  createSortedRowModel,
  filterFn_equals,
  filterFn_includesString,
  filterFn_inNumberRange,
  globalFilteringFeature,
  rowAggregationFeature,
  rowExpandingFeature,
  rowPaginationFeature,
  rowSelectionFeature,
  rowSortingFeature,
  sortFn_alphanumeric,
  sortFn_basic,
  sortFn_datetime,
  sortFn_text,
  tableFeatures,
} from '@tanstack/react-table';

export interface TableFeatureOptions {
  enableFilters?: boolean;
  enableGlobalFiltering?: boolean;
  enableGrouping?: boolean;
  enableSorting?: boolean;
  enablePagination?: boolean;
  enableStructure?: boolean;
  enableRowSelection?: boolean;
  clientSideFiltering?: boolean;
  clientSideGrouping?: boolean;
  clientSideSorting?: boolean;
  clientSidePagination?: boolean;
  clientSideExpanding?: boolean;
}

export const createTableFeatures = ({
  enableFilters = false,
  enableGlobalFiltering = false,
  enableGrouping = false,
  enableSorting = false,
  enablePagination = false,
  enableStructure = false,
  enableRowSelection = false,
  clientSideFiltering = enableFilters || enableGlobalFiltering,
  clientSideGrouping = enableGrouping,
  clientSideSorting = enableSorting,
  clientSidePagination = enablePagination,
  clientSideExpanding = enableGrouping || enableStructure,
}: TableFeatureOptions) => {
  const enableFiltering = enableFilters || enableGlobalFiltering;
  const enableFaceting = enableFilters;
  const enableExpanding = enableGrouping || enableStructure;

  return tableFeatures({
    columnVisibilityFeature,
    columnOrderingFeature,
    ...(enableFiltering && {
      columnFilteringFeature,
      filterFns: {
        equals: filterFn_equals,
        includesString: filterFn_includesString,
        inNumberRange: filterFn_inNumberRange,
      },
    }),
    ...(enableGlobalFiltering && {
      globalFilteringFeature,
    }),
    ...(clientSideFiltering && {
      filteredRowModel: createFilteredRowModel(),
    }),
    ...(enableFaceting && {
      columnFacetingFeature,
      facetedRowModel: createFacetedRowModel(),
      facetedUniqueValues: createFacetedUniqueValues(),
      facetedMinMaxValues: createFacetedMinMaxValues(),
    }),
    ...(enableGrouping && {
      columnGroupingFeature,
      rowAggregationFeature,
      aggregationFns: {
        count: aggregationFn_count,
        max: aggregationFn_max,
        mean: aggregationFn_mean,
        min: aggregationFn_min,
        sum: aggregationFn_sum,
      },
    }),
    ...(clientSideGrouping && {
      groupedRowModel: createGroupedRowModel(),
    }),
    ...(enableExpanding && {
      rowExpandingFeature,
    }),
    ...(clientSideExpanding && {
      expandedRowModel: createExpandedRowModel(),
    }),
    ...(enablePagination && {
      rowPaginationFeature,
    }),
    ...(clientSidePagination && {
      paginatedRowModel: createPaginatedRowModel(),
    }),
    ...(enableRowSelection && {
      rowSelectionFeature,
    }),
    ...(enableSorting && {
      rowSortingFeature,
      sortFns: {
        alphanumeric: sortFn_alphanumeric,
        basic: sortFn_basic,
        datetime: sortFn_datetime,
        text: sortFn_text,
      },
    }),
    ...(clientSideSorting && {
      sortedRowModel: createSortedRowModel(),
    }),
  });
};
